import React, { useEffect, useRef, useState } from 'react';
import { Pressable, View, Text, TextInput, StyleSheet, ScrollView, Platform } from 'react-native';
import Svg, { Polygon } from 'react-native-svg';
import { useTheme, makeThemedStyles, radii, SECTOR_COLOR } from '../../v10/theme';
import { GlassCard } from '../../v10/ui';
import type { Sector } from '../../domain/olmps';
import { STEP_STATUS_META, effectiveStatus, type StepStatus } from '../stepStatus';

export interface PipelineCheckItem {
  id: string;
  text: string;
  checked: boolean;
  /** Pending/Active/Completed — the same field the Directions rows edit. */
  status?: StepStatus;
  substeps?: PipelineCheckItem[];
  dueDate?: string;
}

export interface PipelineProject {
  id: string;
  project_title: string;
  sector?: Sector | null;
  basic_stages: PipelineCheckItem[];
}


const CIRCLE_DIAM = 88;
const COL_WIDTH = 124;
const ARROW_LEN = 36;
// Right edge of the circle, in column-relative x — the arrow must start here
// (not at the column's horizontal center) or it draws straight through the
// circle's own label instead of bridging the gap to the next circle.
const CIRCLE_RIGHT_EDGE = (COL_WIDTH + CIRCLE_DIAM) / 2;

// Same height as the stage circles, so the hexagon sits on the same
// centerline as the pipeline it leads into.
const HEX_HEIGHT = CIRCLE_DIAM;
// A *regular* hexagon (flat top/bottom, points left/right, all 6 sides equal
// length) has height = width * sqrt(3)/2 — so width is derived from height,
// not picked independently, or the sides come out uneven.
const HEX_WIDTH = HEX_HEIGHT * (2 / Math.sqrt(3));
const HEX_FILL = '#8FA532'; // olive — matches the ORDER "Execute" stage color

/** Colour comes straight from the shared STEP_STATUS_META palette keyed off the
 *  row's own status, so a stage/sub-step reads the SAME colour here as it does
 *  in the TKNE Directions list. (This used to be derived positionally —
 *  done/active/future — which drifted from what the list showed.) */
function statusFill(item: PipelineCheckItem): string {
  return STEP_STATUS_META[effectiveStatus(item)].color;
}

function formatShortDate(iso: string): string {
  // Expects yyyy-MM-dd; render as M/D. Falls back to raw string if unparsable.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const month = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  return `${month}/${day}`;
}

/** Flat-top hexagon (points left/right) holding the project title, matching
 *  the reference "SOLVERS V9" badge that leads the stage pipeline. */
function ProjectHexagon({ title, fill, editing, onSubmitTitle }: {
  title: string;
  fill: string;
  /** Rename in place: the label becomes an input; Enter/blur commits. */
  editing?: boolean;
  onSubmitTitle?: (title: string) => void;
}) {
  const { t } = useTheme();
  const styles = useStyles();
  const [draft, setDraft] = useState(title);
  // Enter fires submit AND the blur that follows — commit only once.
  const committed = useRef(false);
  useEffect(() => { if (editing) { setDraft(title); committed.current = false; } }, [editing, title]);
  const commit = () => {
    if (committed.current) return;
    committed.current = true;
    onSubmitTitle?.(draft);
  };
  // 0.25/0.75 (quarter-width top/bottom edges) is what makes this a *regular*
  // hexagon given HEX_WIDTH = HEX_HEIGHT * 2/sqrt(3) above — see that comment.
  const points = `${HEX_WIDTH * 0.25},0 ${HEX_WIDTH * 0.75},0 ${HEX_WIDTH},${HEX_HEIGHT * 0.5} ${HEX_WIDTH * 0.75},${HEX_HEIGHT} ${HEX_WIDTH * 0.25},${HEX_HEIGHT} 0,${HEX_HEIGHT * 0.5}`;
  return (
    <View style={styles.hexWrap}>
      <Svg width={HEX_WIDTH} height={HEX_HEIGHT}>
        <Polygon points={points} fill={fill} stroke={t.goldBright} strokeWidth={3} strokeDasharray="7,5" />
      </Svg>
      {editing ? (
        <View style={styles.hexLabelWrap}>
          <TextInput
            style={styles.hexInput}
            value={draft}
            onChangeText={setDraft}
            onSubmitEditing={commit}
            onBlur={commit}
            autoFocus
            selectTextOnFocus
          />
        </View>
      ) : (
        <View style={styles.hexLabelWrap} pointerEvents="none">
          <Text style={styles.hexLabelText} numberOfLines={2} ellipsizeMode="tail">
            {(title || 'Untitled').toUpperCase()}
          </Text>
        </View>
      )}
    </View>
  );
}

interface ProjectPipelineProps {
  project: PipelineProject;
  onOpen: () => void;
  /** Tapping a stage circle — deep-links to that step in the editor. Falls back to onOpen. */
  onOpenStage?: (stageId: string) => void;
  /** Tapping a sub-box — deep-links to that sub-step in the editor. Falls back to onOpen. */
  onOpenSubstep?: (stageId: string, substepId: string) => void;
  onMenuPress?: () => void;
  /** 'card' (default) = the raised full-width row; 'bare' = no card chrome,
   *  for the TKNE list's group columns where the hexagon IS the item. */
  variant?: 'card' | 'bare';
  /** Bare variant only: when true just the hexagon shows; double-tapping the
   *  hexagon calls onToggleCollapsed to extend the stages out to the right. */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** Bare variant: edit the hexagon's title in place. */
  editingTitle?: boolean;
  onSubmitTitle?: (title: string) => void;
}

// Window for telling a double-tap (expand/collapse) from a single tap (open).
const DOUBLE_TAP_MS = 260;

export function ProjectPipeline({
  project, onOpen, onOpenStage, onOpenSubstep, onMenuPress,
  variant = 'card', collapsed = false, onToggleCollapsed, editingTitle = false, onSubmitTitle,
}: ProjectPipelineProps) {
  const { t } = useTheme();
  const styles = useStyles();
  const stages = project.basic_stages || [];
  const hexFill = project.sector ? SECTOR_COLOR[project.sector] : HEX_FILL;
  const bare = variant === 'bare';

  // Single tap opens, double tap toggles — so the first tap has to wait out
  // the double-tap window before committing to "open".
  const tapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (tapTimer.current) clearTimeout(tapTimer.current); }, []);
  const onHexPress = () => {
    if (!onToggleCollapsed) { onOpen(); return; }
    if (tapTimer.current) {
      clearTimeout(tapTimer.current);
      tapTimer.current = null;
      onToggleCollapsed();
      return;
    }
    tapTimer.current = setTimeout(() => { tapTimer.current = null; onOpen(); }, DOUBLE_TAP_MS);
  };
  // Right-click → the same options menu as long-press (web only; RN-web
  // forwards onContextMenu to the DOM node).
  const contextMenuProps = Platform.OS === 'web' && onMenuPress
    ? { onContextMenu: (e: any) => { e.preventDefault?.(); onMenuPress(); } }
    : {};

  const hexagon = bare ? (
    <Pressable onPress={onHexPress} onLongPress={onMenuPress} disabled={editingTitle} {...(contextMenuProps as any)}>
      <ProjectHexagon title={project.project_title} fill={hexFill} editing={editingTitle} onSubmitTitle={onSubmitTitle} />
    </Pressable>
  ) : (
    <ProjectHexagon title={project.project_title} fill={hexFill} />
  );

  if (bare && collapsed) {
    return <View style={styles.bareContainer}>{hexagon}</View>;
  }

  const Bar = bare ? View : ScrollView;
  const barProps = bare
    ? { style: [styles.orbBar, styles.orbBarBare] }
    : { horizontal: true, showsHorizontalScrollIndicator: false, style: styles.orbScroll, contentContainerStyle: styles.orbBar };

  const body = (
    <Bar {...(barProps as any)}>
        <View style={styles.hexColumn}>
          {hexagon}
          {stages.length > 0 && (
            <View style={styles.hexArrowConnector} pointerEvents="none">
              <View style={styles.arrowLine} />
              <Text style={styles.arrowHead}>{'▶'}</Text>
            </View>
          )}
        </View>

        {stages.length === 0 ? (
          <View style={styles.emptyPipeline}>
            <Text style={styles.emptyPipelineText}>No stages yet</Text>
          </View>
        ) : (
          stages.map((stage, idx) => {
            const substeps = stage.substeps || [];
            const label = `${idx + 1}. ${(stage.text || 'STAGE').toUpperCase()}`;

            return (
              <View key={stage.id} style={styles.stageColumn}>
                {/* Circle — tapping jumps straight to this step in the editor. */}
                <Pressable
                  onPress={(e) => {
                    // Nested inside the card's own onOpen Pressable (and a
                    // horizontal ScrollView) — without this the tap bubbles
                    // up on web and onOpen fires alongside/instead of the
                    // deep-link, or does nothing if onOpen is a no-op.
                    e.stopPropagation?.();
                    onOpenStage ? onOpenStage(stage.id) : onOpen();
                  }}
                  hitSlop={4}
                  style={[styles.stageOrb, { backgroundColor: statusFill(stage) }]}
                >
                  <Text
                    style={[styles.stageOrbText, styles.onStatusFill]}
                    numberOfLines={2}
                    ellipsizeMode="tail"
                  >
                    {label}
                    {stage.dueDate ? `\n${formatShortDate(stage.dueDate)}` : ''}
                  </Text>
                </Pressable>

                {/* Arrow to the next circle, anchored on the circles' shared
                    centerline and overlapping the gap into the next column. */}
                {idx < stages.length - 1 && (
                  <View style={styles.arrowConnector} pointerEvents="none">
                    <View style={styles.arrowLine} />
                    <Text style={styles.arrowHead}>{'▶'}</Text>
                  </View>
                )}

                {/* Sub-boxes hanging below every circle */}
                {substeps.length > 0 && (
                  <View style={styles.subBoxContainer}>
                    <View style={styles.subBoxConnector} />
                    {substeps.map((sub, subIdx) => (
                      <React.Fragment key={sub.id}>
                        {subIdx > 0 && <View style={styles.subBoxConnectorShort} />}
                        <Pressable
                          onPress={(e) => {
                            e.stopPropagation?.();
                            onOpenSubstep ? onOpenSubstep(stage.id, sub.id) : onOpen();
                          }}
                          hitSlop={4}
                          style={[styles.subBox, { backgroundColor: statusFill(sub) }]}
                        >
                          <Text
                            style={[styles.subBoxText, styles.onStatusFill]}
                            numberOfLines={1}
                            ellipsizeMode="tail"
                          >
                            {sub.text || `${idx + 1}.${subIdx + 1}`}
                          </Text>
                        </Pressable>
                      </React.Fragment>
                    ))}
                  </View>
                )}
              </View>
            );
          })
        )}
    </Bar>
  );

  if (bare) return <View style={styles.bareContainer}>{body}</View>;

  return (
    <GlassCard style={styles.pipelineContainer}>
    <Pressable onPress={onOpen} onLongPress={onMenuPress}>
      {onMenuPress ? (
        <Pressable
          onPress={(e) => {
            // Nested inside the row's own Pressable — without this the tap
            // bubbles up on web and also fires onOpen, so the menu opens and
            // is instantly replaced by the design screen.
            e.stopPropagation();
            onMenuPress();
          }}
          hitSlop={10}
          style={styles.menuBtn}
        >
          <Text style={styles.menuDots}>⋯</Text>
        </Pressable>
      ) : null}
      {body}
    </Pressable>
    </GlassCard>
  );
}

const useStyles = makeThemedStyles(({ t, c }) =>
  StyleSheet.create({
  pipelineContainer: {
    borderRadius: radii.panel,
    paddingVertical: 12,
    paddingHorizontal: 12,
    marginBottom: 16,
    width: '100%',
    alignSelf: 'stretch',
    position: 'relative',
  },
  bareContainer: {
    alignSelf: 'flex-start',
  },
  // Bare (column) mode: left-aligned so the stages extend rightward from the
  // hexagon, and no min-height so a collapsed→expanded row doesn't jump.
  orbBarBare: {
    flexGrow: 0,
    justifyContent: 'flex-start',
    paddingVertical: 0,
    paddingHorizontal: 0,
    minHeight: 0,
  },
  menuBtn: {
    position: 'absolute',
    top: 6,
    right: 8,
    zIndex: 2,
    padding: 4,
  },
  menuDots: { fontSize: 18, fontWeight: '800', color: t.ivorySoft },
  emptyPipeline: {
    height: HEX_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  emptyPipelineText: {
    fontSize: 12,
    color: t.ivorySoft,
    fontStyle: 'italic',
  },
  orbScroll: {
    width: '100%',
  },
  orbBar: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    // flexGrow + center: when the diagram's natural content width is
    // narrower than the card, the whole row centers in the available space
    // instead of hugging the left edge. Column widths/gaps stay FIXED
    // (COL_WIDTH) so the connector arrows — which are fixed-length and
    // positioned assuming fixed spacing — always still reach; stretching
    // the gaps (space-between) breaks them. When content overflows, this
    // has no effect and the ScrollView scrolls normally.
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 4,
    minHeight: 160,
  },
  hexColumn: {
    width: HEX_WIDTH + 20,
    alignItems: 'center',
    position: 'relative',
  },
  hexWrap: {
    width: HEX_WIDTH,
    height: HEX_HEIGHT,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hexLabelWrap: {
    position: 'absolute',
    left: HEX_WIDTH * 0.14,
    right: HEX_WIDTH * 0.14,
    top: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hexInput: {
    width: '100%',
    backgroundColor: t.navyRaised,
    borderWidth: 1,
    borderColor: t.gold,
    borderRadius: 6,
    paddingHorizontal: 4,
    paddingVertical: 2,
    fontSize: 12,
    fontWeight: '700',
    textAlign: 'center',
    color: t.ivory,
  },
  hexLabelText: {
    fontSize: 13,
    lineHeight: 15,
    fontWeight: '800',
    color: '#070b17',
    textAlign: 'center',
  },
  hexArrowConnector: {
    position: 'absolute',
    top: HEX_HEIGHT / 2 - 8,
    left: HEX_WIDTH,
    width: ARROW_LEN + 14,
    height: 16,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 1,
  },
  stageColumn: {
    width: COL_WIDTH,
    alignItems: 'center',
    position: 'relative',
  },
  // Arrow sits on the shared circle centerline, anchored to the right edge
  // of this column and extending into the gap before the next column, so
  // it visually connects THIS circle to the NEXT one regardless of how many
  // sub-boxes either stage has stacked below it.
  arrowConnector: {
    position: 'absolute',
    top: CIRCLE_DIAM / 2 - 8,
    left: CIRCLE_RIGHT_EDGE,
    width: ARROW_LEN + 14,
    height: 16,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 1,
  },
  arrowLine: {
    width: ARROW_LEN,
    height: 3,
    backgroundColor: t.ivoryFaint,
  },
  arrowHead: {
    fontSize: 12,
    lineHeight: 12,
    color: t.ivoryFaint,
    marginLeft: -1,
  },
  stageOrb: {
    width: CIRCLE_DIAM,
    height: CIRCLE_DIAM,
    borderRadius: CIRCLE_DIAM / 2,
    borderWidth: 2,
    borderColor: t.navyLine,
    boxShadow: `0 0 14px ${t.goldGlow}`,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  onStatusFill: { color: '#ffffff' },
  stageOrbText: {
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center',
  },
  subBoxContainer: {
    alignItems: 'center',
    marginTop: 0,
  },
  subBoxConnector: {
    width: 2,
    height: 14,
    backgroundColor: t.ivoryFaint,
  },
  subBoxConnectorShort: {
    width: 2,
    height: 10,
    backgroundColor: t.ivoryFaint,
  },
  subBox: {
    width: 84,
    height: 30,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 5,
  },
  subBoxText: {
    fontSize: 13,
    fontWeight: '700',
    textAlign: 'center',
  },
}),
);
